import { GQL } from '@aline/graphql';
import { v4 as uuid4 } from 'uuid';
import { z } from 'zod';

import { PositionSuggestedEditSchema } from './playbook-suggested-edit';
import type { PositionSuggestedEdit } from './playbook-suggested-edit';
import { UserInputError } from './util/errors';
import { compareByRank, getInitialRanks, getRanksBetween } from './util/rank';

/**
 * A single position a playbook rule can be evaluated at: the requirement text
 * plus the guidance the AI uses when commenting on it. Positions are stored as
 * an ordered JSONB array on `PlaybookRule`, so this schema is the only place
 * their shape is enforced — the database cannot check it.
 *
 * `rank` is a fractional index, opaque like every other rank in the codebase:
 * compare them, never parse them, and never compare across rules.
 *
 * `suggestedEdit` is the position's pending AI suggestion, if any. It has to
 * be declared here: every position write funnels through a parse of this
 * schema, and a plain `z.object` strips keys it does not know, so an
 * unregistered field would vanish on the next manual edit.
 */
export const PlaybookRulePositionSchema = z.object({
  aiGuidance: z.string().nullable(),
  id: z.uuid(),
  rank: z.string().min(1),
  ruleText: z.string(),
  suggestedEdit: PositionSuggestedEditSchema.optional(),
});
export type PlaybookRulePosition = z.infer<typeof PlaybookRulePositionSchema>;

/**
 * A rule's positions. Display order comes from `rank`, not from array order:
 * the lowest-ranked position is the Starting Position and the rest are Fallback
 * 1..n. A rule is created with a starting position, but it can be left with
 * none (the last one deleted, by hand or by accepting a suggested delete). Such
 * a rule does not run, and the playbook editor says so; see `getIsRunnableRule`.
 *
 * Ranks are what let a caller name a landing spot *between* two positions
 * without restating the whole list, which is what an AI suggestion that moves
 * several positions at once needs: array indexes collapse every one of them
 * onto the same target, so their relative order could not survive the move.
 *
 * Distinct ranks are required rather than merely expected. Equal ranks fall
 * back to comparing ids, so a duplicate would still sort deterministically —
 * but in an order nobody chose, which is exactly the ambiguity ranks exist to
 * remove.
 */
export const PlaybookRulePositionsSchema = z
  .array(PlaybookRulePositionSchema)
  .refine(
    positions => new Set(positions.map(position => position.rank)).size === positions.length,
    "A playbook rule's positions must have distinct ranks."
  );

/**
 * Outcome of evaluating one position against a document. `Failing` means the
 * position produced at least one suggested change; `Passing` means it produced
 * only passing citations, or nothing at all.
 *
 * Reuses the generated GraphQL enum so the value persisted in JSONB and the
 * value served over the API are the same type, with no mapping layer to drift.
 */
export const PlaybookPositionStatusSchema = z.enum(GQL.PlaybookPositionStatus);
export type PlaybookPositionStatus = z.infer<typeof PlaybookPositionStatusSchema>;

/**
 * The four actions that resolve a rule on a document. Reuses the generated
 * GraphQL enum so the value persisted in the database and the value served
 * over the API are the same type, with no mapping layer to drift.
 */
export const PlaybookRuleResolutionKindSchema = z.enum(GQL.PlaybookRuleResolutionKind);
export type PlaybookRuleResolutionKind = z.infer<typeof PlaybookRuleResolutionKindSchema>;

/**
 * The result of running one position of a rule against a document, stored in
 * the `positionResults` array on `PlaybookRuleJob`. `ruleText`, `aiGuidance`
 * and `rank` are a snapshot taken at run time rather than a reference, so a
 * stored result stays readable after its position is edited, reordered, or
 * deleted.
 *
 * `rank` is snapshotted for the same reason as the text: it says where the
 * position sat *when the run happened*. Reading the live rank back off the rule
 * would reorder a finished run underneath the user whenever the rule's
 * positions are rearranged, and would have nothing to report at all for a
 * result whose position has since been deleted.
 */
export const PlaybookRuleJobPositionResultSchema = z.object({
  aiGuidance: z.string().nullable(),
  explanation: z.string(),
  failedAt: z.iso.datetime().nullable(),
  failedReason: z.string().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  positionId: z.uuid(),
  rank: z.string().min(1),
  ruleText: z.string(),
  status: PlaybookPositionStatusSchema,
});
export type PlaybookRuleJobPositionResult = z.infer<typeof PlaybookRuleJobPositionResultSchema>;

/**
 * A rule job's per-position results. Empty until the rule has been run against
 * the document. Order comes from each result's snapshotted `rank`, so a run
 * reads in the order the rule had at the time it ran.
 */
export const PlaybookRuleJobPositionResultsSchema = z.array(PlaybookRuleJobPositionResultSchema);

/**
 * A run's results in the order the rule had when it ran.
 *
 * Keyed on `positionId` rather than `id`, which is the whole difference between
 * a result and the position it came from: the position may since have been
 * edited or deleted, and the result still describes what was evaluated.
 */
export function sortPositionResultsByRank<TResult extends { positionId: string; rank: string }>(
  results: readonly TResult[]
): TResult[] {
  return [...results].sort((a, b) =>
    compareByRank({ ...a, id: a.positionId }, { ...b, id: b.positionId })
  );
}

/**
 * Text and guidance for a position, before it has an id or a rank. Only the
 * AI suggestion writer passes `suggestedEdit`, to mint a position as a pending
 * `add`; every other creator leaves it out.
 */
export interface PlaybookRulePositionProps {
  ruleText: string;
  aiGuidance?: string | null;
  suggestedEdit?: PositionSuggestedEdit;
}

/**
 * Builds a position with a freshly generated id. Ids are minted here rather
 * than supplied by callers so every position is addressable — by the UI that
 * edits it and by the run results that record which position they came from.
 *
 * The rank is the caller's to choose, because only the caller knows where the
 * position is meant to land. Use `createPlaybookRulePositions` to append or
 * insert a group and have the ranks derived for you.
 */
export function createPlaybookRulePosition(
  props: PlaybookRulePositionProps & { rank: string }
): PlaybookRulePosition {
  return {
    aiGuidance: props.aiGuidance ?? null,
    id: uuid4(),
    rank: props.rank,
    ruleText: props.ruleText,
    ...(props.suggestedEdit ? { suggestedEdit: props.suggestedEdit } : {}),
  };
}

/**
 * Builds a group of positions landing between `after` and `before`, keeping the
 * order they were given in. Pass neither bound to rank a brand-new rule's
 * positions, `after` alone to append to an existing rule.
 *
 * Ranks for the group come from one call so they are spread evenly across the
 * gap, rather than each nesting inside the previous one's — the same reason
 * categories and rules seed their ranks in a single call.
 */
export function createPlaybookRulePositions(
  props: readonly PlaybookRulePositionProps[],
  bounds: { after?: string | null; before?: string | null } = {}
): PlaybookRulePosition[] {
  const ranks = getRanksBetween(bounds.after ?? null, bounds.before ?? null, props.length);

  return props.map((position, index) =>
    createPlaybookRulePosition({ ...position, rank: ranks[index] })
  );
}

/**
 * Reassigns evenly spread ranks to `positions` in the order given, for a
 * caller that has restated the whole list and wants it to mean exactly that.
 *
 * Rewriting every rank is the right move only for a whole-list reorder. A move
 * that touches a few positions should rank just those, via
 * `createPlaybookRulePositions` or `getRankBetween`, so it does not race with
 * concurrent edits to the positions it never mentioned.
 */
export function rerankPlaybookRulePositions(
  positions: readonly PlaybookRulePosition[]
): PlaybookRulePosition[] {
  const ranks = getInitialRanks(positions.length);

  return positions.map((position, index) => ({ ...position, rank: ranks[index] }));
}

/**
 * A rule's positions in display order. Stored arrays are kept in rank order by
 * every write path, so this is normally a no-op — it is here so that a row
 * written before ranks existed, or by hand, still renders in a defined order
 * rather than whatever order it happens to be stored in.
 */
export function sortPlaybookRulePositionsByRank<TPosition extends { id: string; rank: string }>(
  positions: readonly TPosition[]
): TPosition[] {
  return [...positions].sort(compareByRank);
}

/**
 * Moves `positionIds` to sit directly after `afterPositionId` as one contiguous
 * group, keeping the order they are given in. A null `afterPositionId` moves
 * them to the front, ahead of everything that stayed put.
 *
 * This is what an index-based reorder could not express. Three positions moving
 * to the same place all target the one index, so nothing distinguishes them and
 * their order among themselves is lost. Here the gap between two ranks is
 * subdivided into as many ranks as the group needs, so each lands somewhere of
 * its own and the order asked for survives.
 *
 * Sparse on purpose: only the positions named get a new rank, so a caller that
 * describes one move — an AI suggestion, a drag — does not restate positions it
 * never mentioned, and cannot clobber a concurrent edit to them.
 *
 * The upper bound is the next position that is *staying*, not the next one in
 * the current order. A neighbour that is itself part of the group is about to
 * vacate its rank, so bounding against it would crowd the group into a gap that
 * is going to be empty anyway.
 */
export function movePlaybookRulePositions(
  positions: readonly PlaybookRulePosition[],
  options: { positionIds: readonly string[]; afterPositionId?: string | null }
): PlaybookRulePosition[] {
  const { afterPositionId = null, positionIds } = options;
  if (!positionIds.length)
    throw new UserInputError('positionIds must name at least one position to move.');
  if (new Set(positionIds).size !== positionIds.length)
    throw new UserInputError('positionIds must not name the same position twice.');

  const positionById = new Map(positions.map(position => [position.id, position]));
  const moving = positionIds.map(positionId => {
    const position = positionById.get(positionId);
    if (!position) throw new UserInputError(`Position ${positionId} does not belong to this rule.`);

    return position;
  });

  if (afterPositionId !== null && !positionById.has(afterPositionId))
    throw new UserInputError(`Position ${afterPositionId} does not belong to this rule.`);
  const movingIds = new Set(positionIds);
  // Landing after a position that is itself moving describes no place at all:
  // the anchor's own rank is one of the ones being replaced.
  if (afterPositionId !== null && movingIds.has(afterPositionId))
    throw new UserInputError('afterPositionId must name a position that is not being moved.');

  const stayingPut = sortPlaybookRulePositionsByRank(positions).filter(
    position => !movingIds.has(position.id)
  );
  const anchorIndex =
    afterPositionId === null
      ? -1
      : stayingPut.findIndex(position => position.id === afterPositionId);
  const ranks = getRanksBetween(
    // -1 is the front of the list, where `stayingPut[0]` is the upper bound and
    // there is no lower one — which is what the arithmetic below already says.
    anchorIndex === -1 ? null : stayingPut[anchorIndex].rank,
    stayingPut[anchorIndex + 1]?.rank ?? null,
    moving.length
  );

  return sortPlaybookRulePositionsByRank([
    ...stayingPut,
    ...moving.map((position, index) => ({ ...position, rank: ranks[index] })),
  ]);
}

/**
 * The starting position is the lowest-ranked one. Prefer this over indexing
 * directly so the convention stays greppable, and so a caller holding an
 * unsorted list cannot mistake the first element for the starting position.
 *
 * Ranked types only: without a rank there is no defined first element, so a
 * position result — which is a run-time snapshot with its own fixed order —
 * is not something this can answer for.
 *
 * Undefined for a rule left with no positions.
 */
export function getStartingPosition<TPosition extends { id: string; rank: string }>(
  positions: readonly TPosition[]
): TPosition | undefined {
  return sortPlaybookRulePositionsByRank(positions).at(0);
}

/** Everything after the starting position, in display order. */
export function getFallbacks<TPosition extends { id: string; rank: string }>(
  positions: readonly TPosition[]
): TPosition[] {
  return sortPlaybookRulePositionsByRank(positions).slice(1);
}
